import { TrialBalanceReport } from './pages/accounting/reports/TrialBalance';
import React from 'react';
// Self-hosted variable fonts — the app's modern typeface (Plus Jakarta Sans)
// plus Geist Mono for figures — shipped to every user instead of relying on
// whatever font the OS happens to have. Imported here so the @font-face rules
// load before first paint.
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource-variable/geist-mono';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Box, CircularProgress, Typography, Alert, Button, CssVarsProvider, CssBaseline } from '@mui/joy';
import { appTheme } from './theme';
import { ApplicationStyles } from './components/layout/ApplicationStyles';
import { IconContext } from '@phosphor-icons/react';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FeatureGate } from './components/FeatureGate';
import { FeatureFlagProvider } from './hooks/useFeatureFlag';
// REMOVED: UpgradePromptProvider - per-seat billing has no usage limits
import { AppLayout } from './components/layout/AppLayout';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { loadGoogleMapsScript } from './utils/loadGoogleMaps';
import { initSentry } from './lib/sentry';
import { installImpersonationFetch } from './lib/installImpersonationFetch';

// Initialize PDF worker before any react-pdf components are used
// This must be imported early to ensure worker is configured before any PDF rendering
import './lib/pdfWorkerSetup';

// Wrap window.fetch so every same-origin /api/* request carries the
// X-Impersonate-User-Id header when Super Admin impersonation is active.
// Must run before any request fires.
installImpersonationFetch();

// Initialize Sentry before app loads
initSentry();

// Helper component to redirect with params.
//
// Carries the query string across as well as the path params: legacy billing
// routes are reached from links like `/billing/invoices/new?job=12`, and
// dropping the search meant the destination form opened blank and the user
// re-picked the job by hand.
const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  const location = useLocation();
  const path = to.replace(/:(\w+)/g, (_, key) => params[key] || '');
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />;
};

// A fixed-target redirect that still preserves the query string.
const RedirectPreservingQuery: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

// Import actual app components
import { JobBoard } from './pages/JobBoard';
import { JobDetail } from './pages/JobDetail';
import JobBoardSettings from './pages/JobBoardSettings';
import { TasksPage } from './pages/tasks/TasksPage';
import { CrmPageUnified } from './pages/CrmPageUnified';
import { ContactsPage } from './pages/crm/ContactsPage';
import SalesDeskPage from './pages/crm/salesDesk/SalesDeskPage';
import { ContactDetailPage } from './pages/crm/ContactDetailPage';
import { PipelinePage } from './pages/crm/PipelinePage';
import { OpportunityDetailPage } from './pages/crm/OpportunityDetailPage';
import { ContractsPage } from './pages/crm/ContractsPage';
import { ContractDetailPage } from './pages/crm/ContractDetailPage';
import { ContractDesignerPage } from './pages/crm/ContractDesignerPage';
import { ErrorBoundary } from './components/accounting/ErrorBoundary';
import { ContractTemplatesPage } from './pages/crm/ContractTemplatesPage';
import { InPersonSigningInterface } from './components/crm/contracts/InPersonSigningInterface';
import { CrmSettingsPage } from './pages/crm/CrmSettingsPage';
import BillingApp from './BillingApp';
import BillingDashboard from './pages/BillingDashboard';
import EstimateList from './pages/EstimateList';
import EstimateCreate from './pages/EstimateCreate';
import EstimateDetail from './pages/EstimateDetail';
import EstimateEdit from './pages/EstimateEdit';
import InvoiceList from './pages/InvoiceList';
import InvoiceCreate from './pages/InvoiceCreate';
import RecurringInvoices from './pages/RecurringInvoices';
import RecurringInvoiceForm from './pages/RecurringInvoiceForm';
import WorkOrderList from './pages/WorkOrderList';
import InvoiceDetail from './pages/InvoiceDetail';
import InvoiceEdit from './pages/InvoiceEdit';
import PaymentList from './pages/PaymentList';
import PaymentCreate from './pages/PaymentCreate';
import BillingSettings from './pages/BillingSettings';
import { DashboardApp } from './pages/dashboard/DashboardApp';
import { QuickSetupWizard } from './components/setup/QuickSetupWizard';
import { MarketingApp } from './pages/marketing/MarketingApp';
import { WebsiteBuilderList } from './pages/marketing/website-builder/WebsiteBuilderList';
import { WebsiteBuilderWizard } from './pages/marketing/website-builder/WebsiteBuilderWizard';
import WebsiteManager from './pages/marketing/website-builder/WebsiteManager';
import WebsiteGlobalSettings from './pages/marketing/website-builder/WebsiteGlobalSettings';
import { WebsitePreview } from './pages/marketing/website-builder/WebsitePreview';
import VisualEditor from './pages/marketing/website-builder/visual/VisualEditor';
import WebsiteWorkspace from './pages/marketing/website-builder/WebsiteWorkspace';
import WebsiteReviewPreview from './pages/marketing/website-builder/WebsiteReviewPreview';
import { Reviews } from './pages/marketing/Reviews';
import { LocalSEO } from './pages/marketing/LocalSEO';
import { SocialMedia } from './pages/marketing/SocialMedia';
import { ContentHub } from './pages/marketing/ContentHub';
import { Forms } from './pages/marketing/Forms';
import { QRCodes } from './pages/marketing/QRCodes';
import { FormBuilder } from './pages/marketing/FormBuilder';
import { FormEmbed } from './pages/marketing/FormEmbed';
import { FormSubmissions } from './pages/marketing/FormSubmissions';
import { RoofQuoteWidgetPage } from './pages/marketing/RoofQuoteWidget';
import { MediaLibrary } from './pages/marketing/MediaLibrary';
import { MarketingSettings } from './pages/marketing/MarketingSettings';
import { EmailMarketing } from './pages/marketing/EmailMarketing';
import { DirectMailDashboardPage } from './pages/marketing/DirectMailDashboardPage';
import { DirectMailMapPage } from './pages/marketing/DirectMailMapPage';
import { DirectMailQueuePage } from './pages/marketing/DirectMailQueuePage';
import { DirectMailCampaignsPage } from './pages/marketing/DirectMailCampaignsPage';
import { DirectMailBatchesPage } from './pages/marketing/DirectMailBatchesPage';
import { DirectMailSettingsPage } from './pages/marketing/DirectMailSettingsPage';
import { MailerQuotePage } from './pages/public/MailerQuotePage';
import { ArticleView } from './pages/marketing/ArticleView';
import { OAuthCallback } from './components/marketing/OAuthCallback';
import { CMSApp } from './pages/cms/CMSApp';
import { HawkeyeAppUnified } from './pages/hawkeye/HawkeyeAppUnified';
import { HawkeyeDashboard } from './pages/hawkeye/HawkeyeDashboard';
import { AnalysisSubmit } from './pages/hawkeye/AnalysisSubmit';
import { AnalysisDetail } from './pages/hawkeye/AnalysisDetail';
import { AnalysisHistory } from './pages/hawkeye/AnalysisHistory';
import { NotificationsPage } from './pages/NotificationsPage';
import { MeasurementsPage } from './pages/measurements/MeasurementsPage';
import { MeasurementDetailPage } from './pages/measurements/MeasurementDetailPage';
import { UsersPage } from './pages/settings/UsersPage';
import { RolesPermissionsPage } from './pages/settings/RolesPermissionsPage';
import { ProfilePage } from './pages/settings/ProfilePage';
import { OrganizationPage } from './pages/settings/OrganizationPage';
import { CommissionSettingsPage } from './pages/settings/CommissionSettingsPage';
import { WeatherReportSettingsPage } from './pages/settings/WeatherReportSettingsPage';
import { BillingPage } from './pages/settings/BillingPage';
import { SchedulingSettingsPage } from './pages/settings/SchedulingSettingsPage';
import { SettingsAppUnified } from './pages/settings/SettingsApp';
import { ImportDataPage } from './pages/settings/ImportDataPage';
import { MaterialTakeoffSettingsPage } from './pages/settings/MaterialTakeoffSettingsPage';
import { OperationsApp } from './pages/operations/OperationsApp';
import { Scheduling } from './pages/Scheduling';
import { MyMeetingsPage } from './pages/scheduling/MyMeetingsPage';
import { TeamManagementPage } from './pages/scheduling/TeamManagementPage';
import { DocsPage } from './pages/DocsPage';
import PolicyCopilotApp from './apps/PolicyCopilot/PolicyCopilotApp';
import InboxApp from './pages/InboxApp';
import ChatApp from './pages/ChatApp';
import DistributorsApp from './components/distributors/DistributorsApp';
import { ContractSigningPage } from './components/crm/ContractSigningPage';
import { SubcontractorsApp } from './pages/subcontractors/SubcontractorsApp';
import { ReviewFunnelPage } from './pages/public/ReviewFunnelPage';
import ReviewWidgetDisplay from './pages/public/ReviewWidgetDisplay';
import { BookingWidget } from './pages/public/BookingWidget';
import ProposalView from './pages/public/ProposalView';
import HomeownerPage from './pages/public/HomeownerPage';

// HR pages
import {
  HrApp,
  TeamDirectoryPage,
  MyEmployeeFilePage,
  OnboardingDashboardPage,
  TrainingLibraryPage,
  HrSettingsPage,
} from './pages/hr';
import { OnboardingWizard } from './pages/hr/wizard/OnboardingWizard';

// HR Recruiting pages
import { CampaignListPage } from './pages/hr/recruiting/CampaignListPage';
import { CampaignDetailPage } from './pages/hr/recruiting/CampaignDetailPage';
import { CampaignEditPage } from './pages/hr/recruiting/CampaignEditPage';
import { CandidatePipelinePage } from './pages/hr/recruiting/CandidatePipelinePage';
import { PublicApplyPage } from './pages/hr/recruiting/PublicApplyPage';

// Learner training pages (the crew member's own training, not HR admin)
import { MyTrainingPage, TrainingPlayerPage } from './pages/training';

// Proposal pages (SumoQuote-style estimates)
import ProposalHub from './pages/proposals/ProposalHub';
import DealProposalWorkspace from './pages/proposals/DealProposalWorkspace';
import InspectionReportView from './pages/public/InspectionReportView';
import ProposalBuilder from './pages/proposals/ProposalBuilder';
import ProposalDetail from './pages/proposals/ProposalDetail';
import ProposalSettingsPage from './pages/proposals/ProposalSettingsPage';

// Advanced (Automations) pages
import AutomationsListPage from './advanced/automations/AutomationsListPage';
import AutomationEditorPage from './advanced/automations/AutomationEditorPage';

// Checklists pages
import ChecklistsListPage from './advanced/checklists/ChecklistsListPage';
import ChecklistEditorPage from './advanced/checklists/ChecklistEditorPage';

// CallBuddy pages

// Maintenance pages
import { MaintenanceApp } from './pages/maintenance';

// Storm Tracker pages
import { StormTrackerApp } from './pages/stormtracker';

// Commission pages
import CommissionDashboard from './pages/commissions/CommissionDashboard';
import MyCommissions from './pages/commissions/MyCommissions';

// Accounting pages
import { AccountingApp } from './pages/accounting/AccountingApp';
import AccountingSettings from './pages/accounting/AccountingSettings';
import { Dashboard as AccountingDashboard } from './pages/accounting/Dashboard';
import { ChartOfAccounts } from './pages/accounting/ChartOfAccounts';
import { JournalEntries } from './pages/accounting/JournalEntries';
import { Reports as AccountingReports } from './pages/accounting/Reports';
import { ARAgingReport } from './pages/accounting/ARAgingReport';
import { APAgingReport } from './pages/accounting/APAgingReport';
import { JournalEntryForm } from './pages/accounting/JournalEntryForm';
import { BalanceSheet } from './pages/accounting/reports/BalanceSheet';
import { ProfitLoss } from './pages/accounting/reports/ProfitLoss';
import { CashFlow } from './pages/accounting/reports/CashFlow';
import { CustomerBalancesReportPage } from './pages/accounting/reports/CustomerBalancesReport';
import { AccountActivityReportPage } from './pages/accounting/reports/AccountActivityReport';
import { JournalEntriesReport } from './pages/accounting/reports/JournalEntriesReport';
import { FinancialAnalytics } from './pages/accounting/FinancialAnalytics';
import { BankConnections } from './pages/accounting/banking/BankConnections';
import { PeriodManagement } from './pages/accounting/PeriodManagement';
import { BankTransactions } from './pages/accounting/banking/BankTransactions';
import { BankRules } from './pages/accounting/banking/BankRules';
import { BankReconciliation } from './pages/accounting/banking/BankReconciliation';
import { BankingLayout } from './pages/accounting/banking/BankingLayout';
import ChecklistRunPage from './advanced/checklists/ChecklistRunPage';
import ChecklistRunExecutionPage from './advanced/checklists/ChecklistRunExecutionPage';

// Pages (Wiki) pages
import { PagesPage } from './pages/pages/PagesPage';

// Reports pages
import ReportsPage from './pages/reports/ReportsPage';
import CanvasingMapReport from './pages/reports/CanvasingMapReport';
import BillingReports from './pages/BillingReports';
import { SalesReportsPage } from './pages/reports/sales';

// Auth pages
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { SignupSuccessPage } from './pages/auth/SignupSuccessPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { PasswordResetPage } from './pages/auth/PasswordResetPage';
import { TermsOfUsePage } from './pages/legal/TermsOfUsePage';
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage';
import { NotFoundPage, ForbiddenPage, ServerErrorPage } from './pages/error/ErrorPage';

// Affiliate pages
import {
  AffiliateSignupPage,
  AffiliateApplicationSuccessPage,
  AffiliateDashboard
} from './pages/affiliates';

// Affiliate settings pages
import { AffiliateSettingsPage } from './pages/settings/AffiliateSettingsPage';
import { AdminSettingsPage } from './pages/settings/AdminSettingsPage';
import { TagManagementPageContainer } from './pages/settings/TagManagementPageContainer';
import PhoneNumbersPage from './pages/settings/PhoneNumbersPage';
import { QuickBooksPage } from './pages/settings/QuickBooksPage';

// Support pages
import SupportPage from './pages/SupportPage';

// Admin hub (super admin only) — consolidates Organizations, Support,
// Affiliates and CallBuddy tools under one tabbed page.
import AdminHubPage from './pages/admin/AdminHubPage';

// Affiliate utility functions
import { isAffiliate, isSuperAdmin } from './utils/affiliate';

/**
 * Redirect the pre-/operations job URLs, keeping the job you asked for.
 *
 * `/jobs/board/:id` was matched by a `jobs/*` catch-all that redirected to
 * `/operations/jobs` — the board — so the id was dropped and every link to a
 * specific job silently landed on the list instead. Callers now build
 * `/operations/jobs/:id` directly; this keeps old bookmarks, emailed links and
 * anything still holding the previous URLs working.
 */
const LegacyJobRedirect: React.FC = () => {
  const location = useLocation();
  const jobId = location.pathname.match(/^\/jobs\/board\/([^/]+)/)?.[1];
  const target = jobId ? `/operations/jobs/${jobId}` : '/operations/jobs';
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ApiClient retries transient GET/HEAD failures. Avoid multiplying retries
      // here: some query functions use POST, which must not be replayed.
      retry: false,
      refetchOnWindowFocus: false,
    },
      },
});

// Helper component to combine ProtectedRoute + AppLayout
const ProtectedAppLayout: React.FC<{ children: React.ReactNode; requiresOrganization?: boolean }> = ({ 
  children, 
  requiresOrganization = true 
}) => (
  <ProtectedRoute requiresOrganization={requiresOrganization}>
    <AppLayout>
      {children}
    </AppLayout>
  </ProtectedRoute>
);

// Root layout that provides contexts inside Router
// NOTE: UpgradePromptProvider removed - per-seat billing has no usage limits
const RootLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    {children}
    <Toaster position="top-right" />
  </>
);

// Root redirect component that checks user type
const RootRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-md" color="neutral">
          Loading...
        </Typography>
      </Box>
    );
  }
  
  // If user is affiliate-only (no organization), go to affiliate dashboard
  if (user?.is_affiliate && !user?.organization) {
    return <Navigate to="/affiliate-dashboard" replace />;
  }
  
  // Otherwise go to main dashboard
  return <Navigate to="/dashboard" replace />;
};

// Route guard for affiliate-only routes
const AffiliateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading state while authentication is being determined
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 2s linear infinite'
        }} />
        <p>Checking affiliate status...</p>
      </div>
    );
  }
  
  if (!isAffiliate(user)) {
    return <Navigate to="/affiliate-signup" replace />;
  }
  
  return <>{children}</>;
};

// Route guard for super admin routes
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  console.log('🔐 SuperAdminRoute Debug:', {
    user: user,
    loading: loading,
    isSuperAdmin: isSuperAdmin(user),
    userSuperuser: user?.is_superuser,
    userAuthenticated: !!user
  });
  
  // Show loading state while authentication is being determined
  if (loading) {
    console.log('⏳ SuperAdminRoute: Auth still loading, showing loading state');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 2s linear infinite'
        }} />
        <p>Checking permissions...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (!isSuperAdmin(user)) {
    console.log('🚫 SuperAdminRoute: Access denied, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }
  
  console.log('✅ SuperAdminRoute: Access granted');
  return <>{children}</>;
};

function LegacyTaskRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/operations/tasks', search: location.search, hash: location.hash }} replace />;
}

const router = createBrowserRouter([
      {
        path: '',
    element: <RootLayout><Outlet /></RootLayout>,
    children: [
      // Auth routes (no AppLayout - standalone pages)
      {
        path: 'login',
        element: <LoginPage />
      },
      {
        path: 'signup',
    element: <SignupPage />
      },
      {
        path: 'signup/success',
        element: <SignupSuccessPage />
      },
      {
        path: 'signup/verify',
        element: <VerifyEmailPage />
      },
  // Public contract signing (no authentication required)
      {
        path: 'crm/sign/:contractId/:sessionToken',
    element: <ContractSigningPage />
      },
      // Public review funnel page
      {
        path: 'reviews/:orgSlug',
        element: <ReviewFunnelPage />
      },
      // Public widget display page
      {
        path: 'widget/reviews/:widgetId',
        element: <ReviewWidgetDisplay />
      },
      // Public booking widget (no authentication required)
      {
        path: 'book/:orgSlug/team/:teamSlug',
        element: <BookingWidget />
      },
      {
        path: 'book/:orgSlug/:username',
        element: <BookingWidget />
      },
      // Public proposal view (customer-facing with e-signature)
      {
        path: 'homeowner/:token',
        element: <HomeownerPage />,
      },
      {
        path: 'p/:token',
        element: <ProposalView />
      },
      {
        path: 'inspection-report/:token',
        element: <InspectionReportView />
      },
      // Public mailer quote page — where the QR code on a printed estimate lands
      {
        path: 'm/:token',
        element: <MailerQuotePage />
      },
      // Public job application page (no authentication required)
      {
        path: 'apply/:slug',
        element: <PublicApplyPage />
      },
      // Public onboarding wizard (no authentication required)
      {
        path: 'onboarding/:orgSlug/:sessionToken',
        element: <OnboardingWizard />
      },
  // Public form embed (no authentication required)
      {
        path: 'forms/embed/:formId',
    element: <FormEmbed />
      },
      {
        path: 'password-reset',
    element: <PasswordResetPage />
      },

      // Legal documents (public)
      {
        path: 'terms',
        element: <TermsOfUsePage />
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />
      },

  // Error pages
      {
        path: '403',
    element: <ForbiddenPage />
      },
      {
        path: '500',
    element: <ServerErrorPage />
      },
  
      {
        path: '',
    element: <RootRedirect />
      },
      {
        path: 'dashboard',
    element: (
      <ProtectedAppLayout>
        <DashboardApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'setup',
    element: (
      <ProtectedRoute>
        <QuickSetupWizard />
      </ProtectedRoute>
    )
      },
      {
        path: 'scheduling/meetings',
    element: (
      <ProtectedAppLayout>
        <FeatureGate flag="scheduling_enabled" label="Meeting scheduling">
          <MyMeetingsPage />
        </FeatureGate>
      </ProtectedAppLayout>
    )
      },
      {
        path: 'scheduling/teams',
    element: (
      <ProtectedAppLayout>
        <FeatureGate flag="scheduling_enabled" label="Team scheduling">
          <TeamManagementPage />
        </FeatureGate>
      </ProtectedAppLayout>
    )
      },
      {
        path: 'operations',
    element: (
      <ProtectedAppLayout>
        <OperationsApp />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: 'jobs',
        element: <JobBoard />
      },
      {
        path: 'jobs/:jobId',
        element: <JobDetail />
      },
      {
        path: 'jobs/settings',
        element: <JobBoardSettings />
      },
      {
        path: 'tasks',
        element: <TasksPage />
      },
      {
        path: 'measurements',
        element: <MeasurementsPage />
      },
      {
        path: 'measurements/:id',
        element: <MeasurementDetailPage />
      },
      {
        path: 'scheduling',
        element: <Scheduling />
      },
      {
        path: 'billing',
        element: <BillingApp />,
        children: [
          {
            path: '',
            element: <BillingDashboard />
          },
          {
            path: 'estimates',
            element: <EstimateList />
          },
          {
            path: 'estimates/new',
            element: <EstimateCreate />
          },
          {
            path: 'estimates/:id',
            element: <EstimateDetail />
          },
          {
            path: 'estimates/:id/edit',
            element: <EstimateEdit />
          },
          {
            path: 'invoices',
            element: <InvoiceList />
          },
          {
            path: 'invoices/new',
            element: <InvoiceCreate />
          },
          {
            path: 'invoices/:id',
            element: <InvoiceDetail />
          },
          {
            path: 'invoices/:id/edit',
            element: <InvoiceEdit />
          },
          {
            path: 'recurring',
            element: <RecurringInvoices />
          },
          {
            path: 'recurring/new',
            element: <RecurringInvoiceForm />
          },
          {
            path: 'recurring/:id/edit',
            element: <RecurringInvoiceForm />
          },
          {
            path: 'payments',
            element: <PaymentList />
          },
          {
            path: 'payments/new',
            element: <PaymentCreate />
          },
          {
            path: 'settings',
            element: <BillingSettings />
          }
        ]
      },
      {
        path: 'distributors/*',
        element: <DistributorsApp />
      },
      {
        path: 'subcontractors/*',
        element: <SubcontractorsApp />
      },
      {
        path: 'work-orders/*',
        element: <WorkOrderList />
      },
      {
        path: 'commissions',
        element: <CommissionDashboard />
      },
      {
        path: '',
        element: <Navigate to="jobs" replace />
      }
    ]
      },
  // My Commissions (sales rep personal view)
      {
        path: 'my-commissions',
        element: (
          <ProtectedAppLayout>
            <MyCommissions />
          </ProtectedAppLayout>
        ),
      },
  // Accounting routes
      {
        path: 'accounting',
    element: (
      <ProtectedAppLayout>
        <AccountingApp />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: '',
        element: <AccountingDashboard />
      },
      {
        path: 'dashboard',
        element: <AccountingDashboard />
      },
      {
        path: 'chart-of-accounts',
        element: <ChartOfAccounts />
      },
      {
        path: 'journal-entries',
        element: <JournalEntries />
      },
      {
        path: 'journal-entries/new',
        element: <JournalEntryForm />
      },
      {
        path: 'journal-entries/:id',
        element: <JournalEntryForm />
      },
      {
        path: 'journal-entries/:id/edit',
        element: <JournalEntryForm />
      },
      {
        path: 'reports',
        element: <AccountingReports />
      },
      {
        path: 'reports/ar-aging',
        element: <ARAgingReport />
      },
      {
        path: 'reports/ap-aging',
        element: <APAgingReport />
      },
      {
        path: 'reports/balance-sheet',
        element: <BalanceSheet />
      },
      {
        path: 'reports/profit-loss',
        element: <ProfitLoss />
      },
      {
        path: 'reports/trial-balance',
        element: <TrialBalanceReport />
      },
      {
        path: 'reports/cash-flow',
        element: <CashFlow />
      },
      {
        path: 'reports/customer-balances',
        element: <CustomerBalancesReportPage />
      },
      {
        path: 'reports/account-activity',
        element: <AccountActivityReportPage />
      },
      {
        path: 'reports/journal-entries',
        element: <JournalEntriesReport />
      },
      {
        path: 'analytics',
        element: <FinancialAnalytics />
      },
      {
        path: 'reports/general-ledger',
        async lazy() {
          const { GeneralLedgerPage } = await import('./pages/accounting/reports/GeneralLedger');
          return { Component: GeneralLedgerPage };
        }
      },
      {
        path: 'reports/balance-sheet',
        element: <BalanceSheet />
      },
      {
        path: 'reports/profit-loss',
        element: <ProfitLoss />
      },
      {
        path: 'banking',
        element: <BankingLayout />,
        children: [
          {
            index: true,
            element: <BankConnections />
          },
          {
            path: 'connections',
            element: <BankConnections />
          },
          {
            path: 'transactions',
            element: <BankTransactions />
          },
          {
            path: 'rules',
            element: <BankRules />
          },
          {
            path: 'reconciliation',
            element: <BankReconciliation />
          },
        ]
      },
      {
        path: 'periods',
        element: <PeriodManagement />
      },
      {
        path: 'settings',
        element: <AccountingSettings />
      }
    ]
      },
  // Redirect old routes to new operations structure
      {
        path: 'jobs',
    element: <Navigate to="/operations/jobs" replace />
      },
      {
        path: 'jobs/*',
    element: <LegacyJobRedirect />
      },
      {
        path: 'tasks',
    element: <LegacyTaskRedirect />
      },
      {
        path: 'tasks/*',
    element: <LegacyTaskRedirect />
      },
      {
        path: 'measurements',
    element: <Navigate to="/operations/measurements" replace />
      },
      {
        path: 'measurements/*',
    element: <Navigate to="/operations/measurements" replace />
      },
      {
        path: 'scheduling',
    element: <Navigate to="/operations/scheduling" replace />
      },
      {
        path: 'scheduling/*',
    element: <Navigate to="/operations/scheduling" replace />
      },
      {
        path: 'billing',
    element: <Navigate to="/operations/billing" replace />
      },
      {
        path: 'billing/*',
    element: <Navigate to="/operations/billing" replace />
      },
      {
        path: 'billing/estimates/new',
    element: <RedirectPreservingQuery to="/operations/billing/estimates/new" />
      },
      {
        path: 'billing/estimates/:id',
    element: <RedirectWithParams to="/operations/billing/estimates/:id" />
      },
      {
        path: 'billing/invoices/new',
    element: <RedirectPreservingQuery to="/operations/billing/invoices/new" />
      },
      {
        path: 'billing/invoices/:id',
    element: <RedirectWithParams to="/operations/billing/invoices/:id" />
      },
      {
        path: 'billing/invoices/:id/payment',
    element: <RedirectWithParams to="/operations/billing/invoices/:id" />
      },
      {
        path: 'estimates/new',
    element: <RedirectPreservingQuery to="/operations/billing/estimates/new" />
      },
      {
        path: 'estimates/:id',
    element: <RedirectWithParams to="/operations/billing/estimates/:id" />
      },
      {
        path: 'invoices/new',
    element: <RedirectPreservingQuery to="/operations/billing/invoices/new" />
      },
      {
        path: 'invoices/:id',
    element: <RedirectWithParams to="/operations/billing/invoices/:id" />
      },
      {
        path: 'payments/new',
    element: <RedirectPreservingQuery to="/operations/billing/payments/new" />
      },
      {
        path: 'distributors',
    element: <Navigate to="/operations/distributors" replace />
      },
      {
        path: 'distributors/*',
    element: <Navigate to="/operations/distributors" replace />
      },
      {
        path: 'crm',
    element: (
      <ProtectedAppLayout>
        <CrmPageUnified />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: 'sales-desk',
        element: <SalesDeskPage />
      },
      {
        path: 'contacts',
        element: <ContactsPage />
      },
      {
        path: 'contacts/:contactId',
        element: <ContactDetailPage />
      },
      {
        path: 'pipeline',
        element: <PipelinePage />
      },
      {
        path: 'opportunities/:id',
        element: <OpportunityDetailPage />
      },
      {
        path: 'proposals',
        element: <ProposalHub />
      },
      {
        path: 'proposals/settings',
        element: <ProposalSettingsPage />
      },
      {
        path: 'proposals/deals/:id',
        element: <DealProposalWorkspace />
      },
      {
        path: 'proposals/new',
        element: <ProposalBuilder />
      },
      {
        path: 'proposals/:id',
        element: <ProposalDetail />
      },
      {
        path: 'proposals/:id/edit',
        element: <ProposalBuilder />
      },
      {
        path: 'contracts',
        element: <ContractsPage />
      },
      {
        path: 'contracts/:id',
        element: <ContractDetailPage />
      },
      {
        path: 'contracts/:id/design',
        element: (
          <ErrorBoundary
            fallback={
              <Box sx={{ p: 3 }}>
                <Alert color="danger" sx={{ mb: 2 }}>
                  Failed to load PDF viewer. Please try refreshing the page.
                </Alert>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </Box>
            }
          >
            <ContractDesignerPage />
          </ErrorBoundary>
        )
      },
      {
        path: 'contracts/:contractId/sign-in-person',
        element: <InPersonSigningInterface />
      },
      {
        path: 'contracts/templates',
        element: <ContractTemplatesPage />
      },
      {
        path: 'settings',
        element: <CrmSettingsPage />
      },
      {
        path: '',
        element: <Navigate to="contacts" replace />
      }
    ]
      },
      {
        path: 'marketing',
    element: (
      <ProtectedAppLayout>
        <MarketingApp />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: 'reviews',
        element: <Reviews />
      },
      {
        path: 'local-seo',
        element: <LocalSEO />
      },
      {
        path: 'social-media',
        element: <SocialMedia />
      },
      {
        path: 'content',
        element: <ContentHub />
      },
      {
        path: 'article/:articleId',
        element: <ArticleView />
      },
      {
        path: 'forms',
        element: <Forms />
      },
      {
        path: 'forms/:formId/edit',
        element: <FormBuilder />
      },
      {
        path: 'forms/:formId/submissions',
        element: <FormSubmissions />
      },
      {
        path: 'qr-codes',
        element: <QRCodes />
      },
      {
        path: 'roof-quote-widget',
        element: (
          <FeatureGate flag="roof_quote_widget" label="Roof Quote Widget">
            <RoofQuoteWidgetPage />
          </FeatureGate>
        )
      },
      {
        path: 'media-library',
        element: <MediaLibrary />
      },
      {
        path: 'email-marketing',
        element: (
          <FeatureGate flag="email_marketing" label="Email Marketing">
            <EmailMarketing />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailDashboardPage />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail/map',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailMapPage />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail/queue',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailQueuePage />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail/campaigns',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailCampaignsPage />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail/settings',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailSettingsPage />
          </FeatureGate>
        )
      },
      {
        path: 'direct-mail/batches',
        element: (
          <FeatureGate flag="direct_mail" label="Direct Mail">
            <DirectMailBatchesPage />
          </FeatureGate>
        )
      },
      {
        path: 'website-builder',
        element: (
          <FeatureGate flag="website" label="Website Builder">
            <WebsiteBuilderList />
          </FeatureGate>
        )
      },
      {
        path: 'website-builder/new',
        element: <WebsiteBuilderWizard />
      },
      {
        path: 'website-builder/:projectId',
        element: <WebsiteBuilderWizard />
      },
      {
        path: 'website-builder/:projectId/manage',
        element: <WebsiteManager />
      },
      {
        path: 'website-builder/:projectId/settings',
        element: <WebsiteGlobalSettings />
      },
      {
        path: 'website-builder/:projectId/workspace',
        element: <WebsiteWorkspace />
      },
      {
        path: 'website-builder/:projectId/review/:checkpointId',
        element: <WebsiteReviewPreview />
      },
      {
        path: 'website-builder/:projectId/preview',
        element: <WebsitePreview />
      },
      {
        path: 'website-builder/:projectId/visual/:pageId',
        element: <VisualEditor />
      },
      {
        path: 'settings',
        element: <MarketingSettings />
      },
      {
        path: 'oauth/:platform/callback',
        element: <OAuthCallback />
      },
      {
        path: '',
        element: <Navigate to="local-seo" replace />
      }
    ]
      },
      {
        path: 'reports',
    element: (
      <ProtectedAppLayout>
        <ReportsPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'reports/canvasing',
    element: (
      <ProtectedAppLayout>
        <CanvasingMapReport />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'reports/billing',
    element: (
      <ProtectedAppLayout>
        <BillingReports />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'reports/sales',
    element: (
      <ProtectedAppLayout>
        <SalesReportsPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'cms/*',
    element: (
      <ProtectedAppLayout>
        <CMSApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'hawkeye',
    element: (
      <ProtectedAppLayout>
        <HawkeyeAppUnified />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: 'analyze',
        element: <AnalysisSubmit />
      },
      {
        path: 'analysis/:analysisId',
        element: <AnalysisDetail />
      },
      {
        path: 'history',
        element: <AnalysisHistory />
      },
      {
        path: '',
        element: <HawkeyeDashboard />
      }
    ]
      },
  // Redirect old notifications route to new settings structure
      {
        path: 'notifications',
    element: <Navigate to="/settings/notifications" replace />
      },
      {
        path: 'notifications/*',
    element: <Navigate to="/settings/notifications" replace />
      },
      {
        path: 'inbox/*',
    element: (
      <ProtectedAppLayout>
        <InboxApp />
      </ProtectedAppLayout>
    )
      },
      // Team Chat routes
      {
        path: 'chat',
    element: (
      <ProtectedAppLayout>
        <ChatApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'chat/channels/:channelId',
    element: (
      <ProtectedAppLayout>
        <ChatApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'chat/dm/:userId',
    element: (
      <ProtectedAppLayout>
        <ChatApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'chat/job/:jobId',
    element: (
      <ProtectedAppLayout>
        <ChatApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'docs/*',
    element: (
      <ProtectedAppLayout>
        <DocsPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'policy-copilot/*',
    element: (
      <ProtectedAppLayout>
        <PolicyCopilotApp />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'advanced',
    element: (
      <ProtectedAppLayout>
        <AutomationsListPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'advanced/automations',
    element: (
      <ProtectedAppLayout>
        <FeatureGate flag="automations_enabled" label="Automations">
          <AutomationsListPage />
        </FeatureGate>
      </ProtectedAppLayout>
    )
      },
      {
        path: 'advanced/automations/:id',
    element: (
      <ProtectedAppLayout>
        <AutomationEditorPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'advanced/automations/new',
    element: (
      <ProtectedAppLayout>
        <AutomationEditorPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'checklists',
    element: (
      <ProtectedAppLayout>
        <ChecklistsListPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'checklists/new',
    element: (
      <ProtectedAppLayout>
        <ChecklistEditorPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'checklists/:id',
    element: (
      <ProtectedAppLayout>
        <ChecklistEditorPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'checklists/:id/run',
    element: (
      <ProtectedAppLayout>
        <ChecklistRunPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'checklists/runs/:id',
    element: (
      <ProtectedAppLayout>
        <ChecklistRunExecutionPage />
      </ProtectedAppLayout>
    )
      },
      // Maintenance routes
      {
        path: 'maintenance/*',
    element: (
      <ProtectedAppLayout>
        <MaintenanceApp />
      </ProtectedAppLayout>
    )
      },
      // Storm Tracker routes
      {
        path: 'advanced/storm-tracker/*',
    element: (
      <ProtectedAppLayout>
        <FeatureGate flag="storm_tracking" label="Storm Tracker">
          <StormTrackerApp />
        </FeatureGate>
      </ProtectedAppLayout>
    )
      },
      // HR routes (protected)
      {
        path: 'hr',
        element: (
          <ProtectedAppLayout>
            <FeatureGate flag="team_management" label="Team Management">
              <HrApp />
            </FeatureGate>
          </ProtectedAppLayout>
        ),
        children: [
          { path: 'team', element: <TeamDirectoryPage /> },
          { path: 'my-employee-file', element: <MyEmployeeFilePage /> },
          { path: 'recruiting', element: <CampaignListPage /> },
          { path: 'recruiting/:id', element: <CampaignDetailPage /> },
          { path: 'recruiting/:id/edit', element: <CampaignEditPage /> },
          { path: 'recruiting/:id/pipeline', element: <CandidatePipelinePage /> },
          { path: 'onboarding', element: <OnboardingDashboardPage /> },
          { path: 'training', element: <TrainingLibraryPage /> },
          { path: 'settings', element: <HrSettingsPage /> },
          { path: '', element: <Navigate to="team" replace /> },
        ],
      },
      // My Training — the signed-in crew member's own assignments. Deliberately
      // outside /hr, which is HR-admin and 403s for everyone else.
      {
        path: 'training',
        element: (
          <ProtectedAppLayout>
            <FeatureGate flag="team_management" label="Training">
              <Outlet />
            </FeatureGate>
          </ProtectedAppLayout>
        ),
        children: [
          { path: '', element: <MyTrainingPage /> },
          { path: ':progressId', element: <TrainingPlayerPage /> },
        ],
      },
      {
        path: 'pages',
    element: (
      <ProtectedAppLayout>
        <PagesPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'pages/*',
    element: (
      <ProtectedAppLayout>
        <PagesPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'settings',
    element: (
      <ProtectedAppLayout>
        <SettingsAppUnified />
      </ProtectedAppLayout>
    ),
    children: [
      {
        path: 'users',
        element: <UsersPage />
      },
      {
        path: 'roles',
        element: <RolesPermissionsPage />
      },
      {
        path: 'profile',
        element: <ProfilePage />
      },
      {
        path: 'commissions',
        element: <CommissionSettingsPage />
      },
      {
        path: 'weather-reports',
        element: <WeatherReportSettingsPage />
      },
      {
        path: 'organization',
        element: <OrganizationPage />
      },
      {
        path: 'billing',
        element: <BillingPage />
      },
      {
        path: 'notifications',
        element: <NotificationsPage />
      },
      {
        path: 'scheduling',
        element: <SchedulingSettingsPage />
      },
      {
        path: 'tags',
        element: <TagManagementPageContainer />
      },
      {
        path: 'communications',
        element: <PhoneNumbersPage />
      },
      {
        path: 'quickbooks',
        element: <QuickBooksPage />
      },
      {
        path: 'imports',
        element: <ImportDataPage />
      },
      {
        path: 'takeoff',
        element: <MaterialTakeoffSettingsPage />
      },
      {
        path: 'affiliate',
        element: (
          <AffiliateRoute>
            <AffiliateSettingsPage />
          </AffiliateRoute>
        )
      },
      {
        path: 'admin',
        element: (
          <SuperAdminRoute>
            <AdminSettingsPage />
          </SuperAdminRoute>
        )
      },
      {
        path: '',
        element: <Navigate to="profile" replace />
      }
    ]
      },

  // Public Affiliate Signup (no authentication required)
      {
        path: 'affiliate-signup',
    element: <AffiliateSignupPage />
      },
      {
        path: 'affiliate-application-success',
    element: <AffiliateApplicationSuccessPage />
      },

  // Affiliate Dashboard (protected, accessible to all affiliates)
      {
        path: 'affiliate-dashboard',
    element: (
      <ProtectedAppLayout requiresOrganization={false}>
        <AffiliateDashboard />
      </ProtectedAppLayout>
    )
      },

  // Affiliate Settings (for affiliate-only users)
      {
        path: 'settings/affiliate',
    element: (
      <ProtectedAppLayout requiresOrganization={false}>
        <AffiliateSettingsPage />
      </ProtectedAppLayout>
    )
      },

  // Support (all authenticated users)
      {
        path: 'support',
    element: (
      <ProtectedAppLayout>
        <SupportPage />
      </ProtectedAppLayout>
    )
      },
      {
        path: 'support/:ticketId',
    element: (
      <ProtectedAppLayout>
        <SupportPage />
      </ProtectedAppLayout>
    )
      },

  // Unified SuperAdmin hub (super admin only). Every /manage/* tool renders
  // inside AdminHubPage, which derives its active tab from the URL.
      {
        path: 'manage/scorecard/*',
        element: (
          <SuperAdminRoute>
            <ProtectedAppLayout requiresOrganization={false}>
              <AdminHubPage />
            </ProtectedAppLayout>
          </SuperAdminRoute>
        )
      },
      {
        path: 'manage/dashboard',
        element: <SuperAdminRoute><ProtectedAppLayout requiresOrganization={false}><AdminHubPage /></ProtectedAppLayout></SuperAdminRoute>
      },
      {
        path: 'manage',
        element: <Navigate to="/manage/dashboard" replace />
      },
      {
        path: 'manage/accounts/*',
        element: <SuperAdminRoute><ProtectedAppLayout requiresOrganization={false}><AdminHubPage /></ProtectedAppLayout></SuperAdminRoute>
      },
      {
        path: 'manage/organizations',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/support',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        // Splat so nested sub-tabs deep-link (/manage/crm/leads/<id>).
        // AdminHubPage derives its active tab from pathname.startsWith().
        path: 'manage/crm/*',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/development/*',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/onboarding/*',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/affiliates',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/callbuddy',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },
      {
        path: 'manage/orders',
    element: (
      <SuperAdminRoute>
        <ProtectedAppLayout>
          <AdminHubPage />
        </ProtectedAppLayout>
      </SuperAdminRoute>
    )
      },

  // Debug test route
      {
        path: 'debug-route-test',
    element: (
      <ProtectedAppLayout>
        <div style={{ padding: '2rem' }}>
          <h2>🔥 Debug Route Working!</h2>
          <p>This route works! Path: {window.location.pathname}</p>
        </div>
      </ProtectedAppLayout>
    )
      },
  
      // Catch-all 404 route
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]);

// Load Google Maps API before rendering
loadGoogleMapsScript()
  .then(() => {
    console.log('Google Maps loaded, initializing app...');
  })
  .catch((error) => {
    console.error('Failed to load Google Maps:', error);
    // App will still render, but Maps features may not work
  });

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <CssVarsProvider theme={appTheme} defaultMode="light" modeStorageKey="apexos-color-mode">
      <CssBaseline />
      <ApplicationStyles />
    <IconContext.Provider value={{ size: 20, weight: 'regular' }}>
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider 
        maxSnack={3} 
        anchorOrigin={{ 
          vertical: 'top', 
          horizontal: 'right' 
        }}
        autoHideDuration={6000}
      >
        <AuthProvider>
          <ImpersonationProvider>
            <FeatureFlagProvider>
              <RouterProvider router={router} />
            </FeatureFlagProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </SnackbarProvider>
    </QueryClientProvider>
    </IconContext.Provider>
    </CssVarsProvider>
  </React.StrictMode>
);
